import React from "react";
import { intl } from "LocaleWrapper";

import { Card, FormGroup, Table } from "@cofano/cck";
import { Box, Grid2 as Grid, Tab, Tabs, Tooltip } from "@mui/material";
import useTabs from "components/hooks/useTabs";

import { Language, DataFormat } from "utils";

import { LocationFromQuery } from "view/locations/LocationDetails";
import { LocationQuayTabs } from "view/locations/utils/LocationUtils";

import { ChildPropsFromArray } from "types/commons";

import LocationCraneHandlingRatesPopover from "../popover/LocationCraneHandlingRatesPopover";

type Quay = ChildPropsFromArray<LocationFromQuery, "quays">;
type Bolder = ChildPropsFromArray<Quay, "bolders">;
type Crane = ChildPropsFromArray<Quay, "cranes">;

const LocationQuaysDetails = ({ quays }: { quays: Quay[] }) => {
	return quays?.map((quay, index) => (
		<Grid spacing={0} container key={index}>
			<Grid size={12}>
				<Grid spacing={3} container>
					<Grid size={{ xs: 12, lg: 4, md: 4 }}>
						<div style={{ marginBottom: 16 }}>
							<Card title={Language("QUAY")}>
								<FormGroup label={Language("QUAY_NAME")} labelPlacement="left-center" overflow="hidden">
									{quay.name1 || "-"}
								</FormGroup>

								<FormGroup label={Language("QUAY_LENGTH")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.meter(quay.length)}
								</FormGroup>

								<FormGroup label={Language("QUAY_DEPTH")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.meter(quay.depth)}
								</FormGroup>

								<FormGroup label={Language("MAPS_POSITIONS")} labelPlacement="left-center" overflow="hidden">
									{`${quay.latitude || 0}, ${quay.longitude || 0}`}
								</FormGroup>

								<FormGroup
									label={Language("QUAY_LOADING_SPEED_TON")}
									labelPlacement="left"
									labelPaddingTop={0}
									overflow="hidden">
									<div>
										{DataFormat.simpleUnit(quay.loadingSpeedTon || 0, `${intl.formatMessage({ id: "TON" })}/h`)}
									</div>

									<div>
										{DataFormat.simpleUnit(quay.loadingSpeedTeu || 0, DataFormat.UNITS.TEUH)}
									</div>

									<div>
										{DataFormat.simpleUnit(quay.loadingSpeedM3 || 0, DataFormat.UNITS.M3H)}
									</div>

									<div>
										{DataFormat.simpleUnit(quay.loadingSpeedMove || 0, DataFormat.UNITS.MOVH)}
									</div>
								</FormGroup>

								<FormGroup label={Language("QUAY_DISCHARGING_SPEED_TON")} labelPlacement="left" overflow="hidden">
									<div>
										{DataFormat.simpleUnit(quay.dischargingSpeedTon || 0, `${intl.formatMessage({ id: "TON" })}/h`)}
									</div>

									<div>
										{DataFormat.simpleUnit(quay.dischargingSpeedTeu || 0, DataFormat.UNITS.TEUH)}
									</div>

									<div>
										{DataFormat.simpleUnit(quay.dischargingSpeedM3 || 0, DataFormat.UNITS.M3H)}
									</div>

									<div>
										{DataFormat.simpleUnit(quay.dischargingSpeedMove || 0, DataFormat.UNITS.MOVH)}
									</div>
								</FormGroup>
							</Card>
						</div>
					</Grid>

					<Grid size={{ xs: 12, lg: 8, md: 8 }}>
						<LocationQuay quay={quay} quayIndex={index} />
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	));
};

const LocationQuay = ({ quay, quayIndex }: { quay: Quay, quayIndex: number }) => {

	const { selectedTab, selectTab } = useTabs<LocationQuayTabs>({
		defaultSelected: LocationQuayTabs.BOLDERS,
		param: `${quay.name1}-${quayIndex}`, 
	});

	return (
		<div style={{ marginBottom: 16 }}>
			<Card disablePadding>
				<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
					<Tabs
						value={selectedTab}
						onChange={(_, value) => selectTab(value)}
						indicatorColor="primary"
						variant="scrollable">
						<Tab value={LocationQuayTabs.BOLDERS} label={Language("BOLDERS")} />
						{quay?.bolders?.length ? (
							<Tab value={LocationQuayTabs.CRANES} label={Language("CRANES")} />
						) : (
							<Tooltip title={intl.formatMessage({ id: "ADD_SAVE_BOLDERS_FIRST" })} >
								<span>
									<Tab value={LocationQuayTabs.CRANES} label={Language("CRANES")} disabled />
								</span>
							</Tooltip>
						)}
					</Tabs>
				</Box>

				<div style={{ padding: 16 }}>
					{selectedTab === LocationQuayTabs.BOLDERS && (
						<Bolders bolders={quay.bolders || undefined} />
					)}

					{selectedTab === LocationQuayTabs.CRANES && (
						<Cranes cranes={quay.cranes || undefined} />
					)}
				</div>
			</Card>
		</div>
	);
};

const Bolders = ({ bolders }: { bolders?: Bolder[] }) => {
	return (
		<React.Fragment>
			<Table
				id="locationBoldersTable"
				content={(bolders || []).map(it => ({ ...it, id: it.id || 0 }))}
				columns={[ {
					id: "description",
					title: intl.formatMessage({ id: "BOLDER_DESCRIPTION" }),
				}, {
					id: "latitude",
					title: intl.formatMessage({ id: "BOLDER_LATITUDE" }),
				}, {
					id: "longitude",
					title: intl.formatMessage({ id: "BOLDER_LONGITUDE" }),
				}, {
					id: "metersToNextBolder",
					title: intl.formatMessage({ id: "BOLDER_METERS_TO_NEXT_BOLDER" }),
					render: (row) => DataFormat.meter(row.metersToNextBolder),
				}, {
					id: "phantomBolder",
					title: intl.formatMessage({ id: "BOLDER_PHANTOM_BOLDER" }),
					render: (row) => DataFormat.boolean(row.phantomBolder),
				}, {
					id: "mooringSide",
					title: intl.formatMessage({ id: "BOLDER_MOORING_SIDE" }),
					render: (row) => row.mooringSide && intl.formatMessage({ id: `BOLDER_MOORING_SIDE_${row.mooringSide}` }),
				} ]}
				disableFooter />
		</React.Fragment>
	);
};

const Cranes = ({ cranes }: { cranes?: Crane[] }) => {

	const isContainerCrane = cranes?.[0]?.craneType === "CONTAINER";

	return (
		<React.Fragment>
			<Table
				id="locationCranesTable"
				content={(cranes || []).map(it => ({ ...it, id: it.id || 0 }))}
				columns={[ {
					id: "name",
					title: intl.formatMessage({ id: "CRANE_NAME" }),
				}, {
					id: "sequenceNumber",
					title: intl.formatMessage({ id: "SEQUENCE_NUMBER" }),
				}, {
					id: "craneType",
					title: intl.formatMessage({ id: "CRANE_TYPE" }),
					render: row => intl.formatMessage({ id: `CRANE_TYPE_${row.craneType === "CONTAINER" ? "1" : "2"}` }),
				}, {
					id: "fromBolder.description",
					title: intl.formatMessage({ id: "CRANE_BOLDER_FROM" }),
				}, {
					id: "toBolder.description",
					title: intl.formatMessage({ id: "CRANE_BOLDER_TO" }),
				}, {
					id: "averageCraneSpeedInMovesPerHour",
					title: intl.formatMessage({ id: "CRANE_SPEED" }),
					render: row => DataFormat.simpleUnit(row.averageCraneSpeedInMovesPerHour || 0, DataFormat.UNITS.MH),
					disabled: !isContainerCrane,
				}, {
					id: "maxHeightInMeters",
					title: intl.formatMessage({ id: "CRANE_HEIGHT" }),
					render: row => DataFormat.meter(row.maxHeightInMeters),
					disabled: !isContainerCrane,
				}, {
					id: "maxWidthInMeters",
					title: intl.formatMessage({ id: "CRANE_MAXWIDTH" }),
					render: row => DataFormat.meter(row.maxWidthInMeters),
					disabled: !isContainerCrane,
				}, {
					id: "outreachInContainers",
					title: intl.formatMessage({ id: "CRANE_OUTREACH" }),
					disabled: !isContainerCrane,
				}, {
					id: "liftCapacityInTons",
					title: intl.formatMessage({ id: "CRANE_LIFT_CAPACITY" }),
					render: row => DataFormat.simpleUnit(row.liftCapacityInTons || 0, intl.formatMessage({ id: "TON" })),
					disabled: !isContainerCrane,
				}, {
					id: "twinningAllowed",
					title: intl.formatMessage({ id: "CRANE_TWINNING_ALLOWED" }),
					render: row => DataFormat.boolean(row.twinningAllowed),
					disabled: !isContainerCrane,
				}, {
					id: "fullContainersAllowed",
					title: intl.formatMessage({ id: "CRANE_CAN_HANDLE_FULL_CONTAINERS" }),
					render: row => DataFormat.boolean(row.fullContainersAllowed),
					disabled: !isContainerCrane,
				}, {
					id: "canHandleOOG",
					title: intl.formatMessage({ id: "CRANE_CAN_HANDLE_OOG" }),
					render: row => DataFormat.boolean(row.canHandleOOG),
					disabled: !isContainerCrane,
				}, {
					id: "canHandleBundles",
					title: intl.formatMessage({ id: "CRANE_CAN_HANDLE_BUNDLES" }),
					render: row => DataFormat.boolean(row.canHandleBundles),
					disabled: !isContainerCrane,
				}, {
					id: "handlingRates",
					title: intl.formatMessage({ id: "HANDLING_SPEEDS" }),
					render: (row: Crane) => <LocationCraneHandlingRatesPopover handlingRates={row.handlingRates ?? []} />,
					width: 100,
					disabled: isContainerCrane,
				} ]}
				disableFooter />
		</React.Fragment>
	);
};

export default LocationQuaysDetails;
